import { pointerTo } from "./envelopePointer";
import { BpPhase, BpService, BpSubservice } from "./types";

// An envelope attribute the author may simply not have set. Defaulting a missing
// override-hours to 0 (or a missing quantity to 1) and writing that back on the
// next save is what froze literal zeros into stored payloads, where they are no
// longer distinguishable from a deliberate zero -- and once one is there, the API
// can no longer fill the catalog's value in its place. Payload values arrive as
// either numbers or numeric strings depending on who wrote the envelope.
const authoredNumber = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === "") return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

// Applies only the values the editor actually holds, so an unset row leaves the
// envelope's own value (and the absence of one) untouched.
const withAuthored = (
  attributes: Record<string, any>,
  updates: Record<string, any>
): Record<string, any> => {
  const result = { ...attributes };
  Object.entries(updates).forEach(([key, value]) => {
    if (value !== undefined) result[key] = value;
  });
  return result;
};

// The token a relationship pointer names its target by. A pointer carries `id`
// when the target envelope names an existing record and `lid` when the target is
// itself a local envelope (see envelopePointer.ts) -- reading `id` alone treats
// every lid pointer as absent.
export const pointerToken = (relationship: any): string | undefined => {
  const token = relationship?.data?.id ?? relationship?.data?.lid;
  return token === null || token === undefined ? undefined : String(token);
};

// ── Payload parsing ────────────────────────────────────────────────────────────

export const parsePayload = (
  payload: any[],
  serviceType: "professional_services" | "managed_services",
  defaultPhases: BpPhase[]
): { phases: BpPhase[]; services: BpService[] } => {
  const existingPhases = payload.filter((p) => p.type === "project-phases");
  const phases: BpPhase[] =
    serviceType === "professional_services"
      ? existingPhases?.length
        ? existingPhases
            .map((p) => ({
              id: String(p.id ?? p.lid),
              name: p.attributes?.name ?? "",
              position: p.attributes?.position ?? 0,
            }))
            .sort((a, b) => a.position - b.position)
        : defaultPhases
      : [];

  const subMap: Record<string, BpSubservice[]> = {};
  payload
    .filter((p) => p.type === "project-subservices")
    .forEach((p) => {
      // Every sub-item ProjectContent::EnvelopeBuilder generates names its parent
      // by `lid`. Reading `id` alone collapsed all of them under "", where they
      // matched no service and rendered nowhere. Services are keyed the same way
      // (`ps.id ?? ps.lid`), so the two agree.
      const serviceId = pointerToken(p.relationships?.["project-service"]) ?? "";
      if (!subMap[serviceId]) subMap[serviceId] = [];
      subMap[serviceId].push({
        id: String(p.id ?? p.lid),
        name: p.attributes?.name ?? "",
        position: p.attributes?.position ?? 0,
        quantity: authoredNumber(p.attributes?.quantity),
        hours: authoredNumber(p.attributes?.["override-hours"]),
        resourceId: pointerToken(p.relationships?.["project-resource"]) ?? null,
      });
    });
  Object.values(subMap).forEach((arr) =>
    arr.sort((a, b) => a.position - b.position)
  );

  const services: BpService[] = payload
    .filter(
      (p) =>
        p.type === "project-services" &&
        (p.attributes?.["service-type"] ?? "professional_services") ===
          serviceType
    )
    .map((ps) => {
      const phaseId =
        pointerToken(ps.relationships?.["project-phase"]) || phases?.[0]?.id || "";
      return {
        id: String(ps.id ?? ps.lid),
        catalogId:
          ps.relationships?.service?.data?.id != null
            ? String(ps.relationships.service.data.id)
            : null,
        name: ps.attributes?.name ?? "",
        position: ps.attributes?.position ?? 0,
        serviceType,
        phaseId:
          serviceType === "professional_services" ? String(phaseId) : null,
        subservices: subMap[String(ps.id ?? ps.lid)] ?? [],
        quantity: authoredNumber(ps.attributes?.quantity),
        hours: authoredNumber(ps.attributes?.["override-hours"]),
        resourceId: pointerToken(ps.relationships?.["project-resource"]) ?? null,
      };
    })
    .sort((a, b) => a.position - b.position);

  return { phases, services };
};

// ── Payload reconstruction ─────────────────────────────────────────────────────

export const buildUpdatedPayload = (
  rawPayload: any[],
  serviceType: "professional_services" | "managed_services",
  services: BpService[]
): any[] => {
  const serviceUpdates = new Map(services.map((s) => [s.id, s]));
  const subserviceUpdates = new Map(
    services.flatMap((s) => s.subservices.map((sub) => [sub.id, sub]))
  );

  return rawPayload.map((item) => {
    const id = String(item.id ?? item.lid);

    if (
      item.type === "project-services" &&
      (item.attributes?.["service-type"] ?? "professional_services") ===
        serviceType
    ) {
      const upd = serviceUpdates.get(id);
      if (!upd) return item;
      const updated: any = {
        ...item,
        attributes: withAuthored(item.attributes ?? {}, {
          position: upd.position,
          quantity: upd.quantity,
          "override-hours": upd.hours,
        }),
        relationships: {
          ...item.relationships,
          ...(serviceType === "professional_services" && upd.phaseId !== null
            ? {
                "project-phase": pointerTo(
                  rawPayload,
                  "project-phases",
                  upd.phaseId
                ),
              }
            : {}),
          "project-resource": pointerTo(
            rawPayload,
            "project-resources",
            upd.resourceId
          ),
        },
      };
      return updated;
    }

    if (item.type === "project-subservices") {
      const upd = subserviceUpdates.get(id);
      if (!upd) return item;
      return {
        ...item,
        attributes: withAuthored(item.attributes ?? {}, {
          quantity: upd.quantity,
          "override-hours": upd.hours,
        }),
        relationships: {
          ...item.relationships,
          "project-resource": pointerTo(
            rawPayload,
            "project-resources",
            upd.resourceId
          ),
        },
      };
    }

    return item;
  });
};
